import React, { useMemo, useState } from "react";
import { createClient } from "../../../utils/api";

export default function NewClientModal({
  isOpen,
  onClose,
  onCreated,
  suggestedNextNumber,
}) {
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const [clientNumber, setClientNumber] = useState("");
  const [branchNo, setBranchNo] = useState(""); // opcional -> NULL
  const [branchName, setBranchName] = useState("");

  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  // ✅ Dirección separada
  const [address, setAddress] = useState("");     // calle + número
  const [locality, setLocality] = useState("");   // localidad/ciudad
  const [province, setProvince] = useState("");   // provincia

  const [cuit, setCuit] = useState("");

  // ✅ Emails de facturación
  const [billingEmail1, setBillingEmail1] = useState("");
  const [billingEmail2, setBillingEmail2] = useState("");

  const preview = useMemo(() => {
    const cn = clientNumber.trim();
    const bn = branchNo.trim();
    if (!cn) return "—";
    return bn ? `${cn} ${bn}` : `${cn}`;
  }, [clientNumber, branchNo]);

  const onlyDigits = (v) => v.replace(/[^\d]/g, "");

  const reset = () => {
    setErr("");
    setClientNumber("");
    setBranchNo("");
    setBranchName("");
    setName("");
    setContact("");
    setEmail("");
    setPhone("");

    setAddress("");
    setLocality("");
    setProvince("");

    setCuit("");

    setBillingEmail1("");
    setBillingEmail2("");
  };

  const close = () => {
    reset();
    onClose?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");

    const cn = clientNumber.trim();
    const bn = branchNo.trim();

    if (!cn) return setErr("Falta el N° de cliente.");
    if (!name.trim()) return setErr("Falta Nombre / Razón social.");

    const be1 = billingEmail1.trim();
    const be2 = billingEmail2.trim();
    if (be1 && be2 && be1.toLowerCase() === be2.toLowerCase()) {
      return setErr("Los emails de facturación 1 y 2 no pueden ser iguales.");
    }

    try {
      setSaving(true);

      const payload = {
        client_number: Number(cn),
        branch_no: bn ? Number(bn) : null, // NULL si no se completa
        branch_name: branchName.trim() || null,

        name: name.trim(),
        contact: contact.trim() || null,
        email: email.trim() || null,
        phone: phone.trim() || null,

        // ✅ NUEVO: campos separados
        address: address.trim() || null,     // calle + número
        locality: locality.trim() || null,   // localidad/ciudad
        province: province.trim() || null,   // provincia

        cuit: cuit.trim() || null,
        tax_id: cuit.trim() || null,

        // ✅ Facturación
        billing_email_1: be1 || null,
        billing_email_2: be2 || null,
      };

      const created = await createClient(payload);
      onCreated?.(created);
      close();
    } catch (error) {
      setErr(error?.message || "Error creando cliente");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-lg max-h-[calc(100vh-2rem)] overflow-hidden rounded-2xl border border-white/10 bg-neutral-950 text-white shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
          <div className="text-xl font-semibold">Nuevo cliente</div>
          <button onClick={close} className="text-white/70 hover:text-white">
            Cerrar
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          {/* Body */}
          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-4">
            {err ? (
              <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">
                {err}
              </div>
            ) : null}

            <div className="rounded-xl border border-white/10 bg-white/5 p-4">
              <div className="text-sm font-medium text-white/90 mb-2">
                Código cliente (N° + sucursal)
                {suggestedNextNumber ? (
                  <span className="ml-2 text-xs text-white/60">
                    Sugerido: {suggestedNextNumber}
                  </span>
                ) : null}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-white/60">N° cliente *</label>
                  <input
                    value={clientNumber}
                    onChange={(e) => setClientNumber(onlyDigits(e.target.value))}
                    className="mt-1 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 outline-none focus:border-white/25"
                    placeholder="1088"
                  />
                </div>

                <div>
                  <label className="text-xs text-white/60">
                    N° sucursal (opcional)
                  </label>
                  <input
                    value={branchNo}
                    onChange={(e) => setBranchNo(onlyDigits(e.target.value))}
                    className="mt-1 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 outline-none focus:border-white/25"
                    placeholder="1, 2, 3…"
                  />
                </div>
              </div>

              <div className="mt-2 text-xs text-white/60">
                Se guardará como:{" "}
                <span className="text-white/90 font-medium">{preview}</span>
                {!branchNo.trim() ? (
                  <span className="ml-2 text-white/50">
                    (sin sucursal → branch_no NULL)
                  </span>
                ) : null}
              </div>
            </div>

            <div>
              <label className="text-sm text-white/80">Sucursal / Planta</label>
              <input
                value={branchName}
                onChange={(e) => setBranchName(e.target.value)}
                className="mt-1 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 outline-none focus:border-white/25"
                placeholder="Ej: Anchorena esq. Lateral Acc. Sur"
              />
            </div>

            <div>
              <label className="text-sm text-white/80">
                Nombre / Razón social *
              </label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 outline-none focus:border-white/25"
              />
            </div>

            <div>
              <label className="text-sm text-white/80">Contacto</label>
              <input
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                className="mt-1 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 outline-none focus:border-white/25"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-white/80">Email</label>
                <input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 outline-none focus:border-white/25"
                />
              </div>
              <div>
                <label className="text-sm text-white/80">Teléfono</label>
                <input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 outline-none focus:border-white/25"
                />
              </div>
            </div>

            {/* Emails facturación */}
            <div className="rounded-xl border border-white/10 bg-white/5 p-4">
              <div className="text-sm font-medium text-white/90 mb-2">
                Emails de facturación
                <div className="text-xs text-white/60 font-normal mt-0.5">
                  Se usan para enviar facturas/OC/remitos (distinto al email de
                  contacto).
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-white/60">Facturación 1</label>
                  <input
                    value={billingEmail1}
                    onChange={(e) => setBillingEmail1(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 outline-none focus:border-white/25"
                    placeholder="facturacion@cliente.com"
                  />
                </div>

                <div>
                  <label className="text-xs text-white/60">Facturación 2</label>
                  <input
                    value={billingEmail2}
                    onChange={(e) => setBillingEmail2(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 outline-none focus:border-white/25"
                    placeholder="cuentasapagar@cliente.com"
                  />
                </div>
              </div>
            </div>

            {/* ✅ Dirección separada */}
            <div>
              <label className="text-sm text-white/80">Dirección (calle y número)</label>
              <input
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                className="mt-1 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 outline-none focus:border-white/25"
                placeholder="Ej: B° Judiciales, Manzana G, C-6"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-white/80">Localidad</label>
                <input
                  value={locality}
                  onChange={(e) => setLocality(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 outline-none focus:border-white/25"
                  placeholder="Ej: Rivadavia"
                />
              </div>

              <div>
                <label className="text-sm text-white/80">Provincia</label>
                <input
                  value={province}
                  onChange={(e) => setProvince(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 outline-none focus:border-white/25"
                  placeholder="Ej: San Juan"
                />
              </div>
            </div>

            <div>
              <label className="text-sm text-white/80">Tax ID / CUIT</label>
              <input
                value={cuit}
                onChange={(e) => setCuit(onlyDigits(e.target.value))}
                className="mt-1 w-full rounded-lg border border-white/10 bg-black/40 px-3 py-2 outline-none focus:border-white/25"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-white/10 bg-neutral-950/90 backdrop-blur px-6 py-4">
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={close}
                className="rounded-lg border border-white/15 px-4 py-2 text-white/90 hover:bg-white/5"
              >
                Cancelar
              </button>
              <button
                disabled={saving}
                className="rounded-lg bg-emerald-600 px-4 py-2 font-semibold text-white hover:bg-emerald-500 disabled:opacity-60"
              >
                {saving ? "Creando..." : "Crear cliente"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
